import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, FilmStrip, UploadSimple, CircleNotch, Sparkle, ArrowRight, ArrowSquareOut, CheckCircle, Star } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { collectDropped, slugify } from "../../lib/dropped";
import { looksLikeScenepack, parseScenepackName } from "../../lib/parseScenepackName";
import { ImdbBadge, RtBadge } from "../RatingBadges";
import SmartImg from "../SmartImg";
import PersonPicker from "../PersonPicker";

/** A movie card that already exists on the site. */
export const cardFromMovie = (m) => ({
  mode: "existing",
  slug: m.slug,
  title: m.movie_title,
  year: m.year || 0,
  genre: m.genre || "",
  poster: m.thumbnail_url || "",
  imdb_url: m.imdb_url || "",
  rt_url: m.rt_url || "",
  imdb_rating: m.imdb_rating || 0,
  rt_rating: m.rt_rating || 0,
  packs: m.packs_count || 0,
  characters: m.characters_count || 0,
});

/** A brand-new movie card, from what the server found (or just the typed title). */
export const cardFromInfo = (info, typedTitle) => ({
  mode: "new",
  slug: info?.found ? info.key : slugify(typedTitle) || "untitled",
  title: info?.found ? info.title : typedTitle,
  year: info?.year || 0,
  genre: info?.genre || "Action",
  poster: "", // the creator's own design goes here
  suggestedPoster: info?.poster_url || "", // the IMDb poster, only used if they choose to
  imdb_url: info?.imdb_url || "",
  rt_url: info?.rt_url || "",
  rt_verified: !!info?.rt_verified,
  imdb_rating: info?.imdb_rating || 0,
  rt_rating: info?.rt_rating || 0,
  type: info?.type || "",
  synopsis: info?.synopsis || "",
  candidates: info?.candidates || [],
  found: !!info?.found,
});

/** A celebrity's card: the person is the "movie", and their photo is the poster to start with. */
export const cardFromPerson = (person) => ({
  mode: "new",
  celebrity: true,
  person,
  slug: slugify(person.name) || "untitled",
  title: person.name,
  year: 0,
  genre: "Celebrity",
  poster: person.photo_url || "",
  imdb_url: person.imdb_url || "",
  rt_url: "",
  imdb_rating: 0,
  rt_rating: 0,
  candidates: [],
  found: true,
});

export default function MoviePicker({ onPick, presetTitle = "" }) {
  const [movies, setMovies] = useState(null);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [celebMode, setCelebMode] = useState(false); // adding a celebrity instead of a movie
  const [title, setTitle] = useState(presetTitle);
  const [year, setYear] = useState("");
  const [looking, setLooking] = useState(false);
  const [info, setInfo] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    api.get("/movies?limit=500").then((r) => setMovies(r.data)).catch(() => setMovies([]));
  }, []);

  // a movie named by a request: open it, or start a new card for it
  useEffect(() => {
    if (!presetTitle || movies === null) return;
    const hit = movies.find((m) => m.slug === slugify(presetTitle));
    if (hit) onPick(cardFromMovie(hit), []);
    else { setCreating(true); lookup(presetTitle, ""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movies !== null]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (movies || []).filter((m) => !q || m.movie_title.toLowerCase().includes(q));
  }, [movies, query]);

  // ---------- new card: find the real movie ----------
  const lookup = async (t = title, y = year) => {
    if (!t.trim()) return;
    setLooking(true);
    setInfo(null);
    try {
      const { data } = await api.get("/movies/lookup", { params: { title: t.trim(), year: y || undefined } });
      setInfo(data);
      if (data.found) setTitle(data.title);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't look that up — you can still continue");
      setInfo({ found: false, candidates: [] });
    }
    setLooking(false);
  };

  const pickCandidate = async (imdbId) => {
    try {
      const { data } = await api.get("/movies/resolve", { params: { imdb: imdbId } });
      setInfo(data);
      setTitle(data.title);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load that title");
    }
  };

  const continueWith = () => {
    if (info?.existing) {
      const hit = (movies || []).find((m) => m.slug === info.key);
      onPick(hit ? cardFromMovie(hit) : { ...cardFromInfo(info, title), mode: "existing", poster: info.poster_url }, []);
    } else {
      onPick(cardFromInfo(info?.found ? info : null, title.trim()), []);
    }
  };

  // ---------- "just drop the files, find the movie" ----------
  const detectFromFiles = async (items) => {
    const videos = items.filter((i) => looksLikeScenepack(i.file.name));
    if (!videos.length) return toast("Drop scenepack video files here — or pick a movie below.");
    setDetecting(true);
    try {
      const hints = videos.slice(0, 40).map((i, n) => {
        const p = parseScenepackName(i.file.name, i.path);
        return { id: String(n), name: i.file.name, character: p.character, movie: p.movie, label: p.title };
      });
      const { data } = await api.post("/scenepacks/analyze", { files: hints });
      const keys = [...new Set(data.files.map((f) => f.movie_key).filter(Boolean))];
      if (keys.length !== 1) {
        const names = keys.map((k) => data.movies[k]?.title || k).join(", ");
        toast.error(keys.length ? `These files are from different movies (${names}). Open one movie card at a time.` : "Couldn't tell which movie these are — pick or create the movie card below.");
      } else {
        const found = data.movies[keys[0]];
        const hit = found.existing ? (movies || []).find((m) => m.slug === found.key) : null;
        onPick(hit ? cardFromMovie(hit) : cardFromInfo(found, found.title), items);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't identify these files — pick the movie card below.");
    }
    setDetecting(false);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    detectFromFiles(await collectDropped(e.dataTransfer));
  };

  // =====================================================================
  if (celebMode) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="new-celebrity-form">
        <button type="button" onClick={() => setCelebMode(false)} className="text-[11px] font-mono uppercase tracking-wider text-white/50 hover:text-white mb-6">← Back to all movies</button>
        <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#FF8090] mb-2">New celebrity card</div>
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-2">Who is it?</h2>
        <p className="text-white/50 text-sm mb-6">Search the celebrity. Their photo, bio and IMDb link come from the database — you add the scenepacks.</p>
        <PersonPicker onPick={(person) => onPick(cardFromPerson(person), [])} autoFocus testId="celebrity-picker" />
      </div>
    );
  }

  if (creating) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="new-card-form">
        <button type="button" onClick={() => { setCreating(false); setInfo(null); }} className="text-[11px] font-mono uppercase tracking-wider text-white/50 hover:text-white mb-6">← Back to all movies</button>
        <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#FF8090] mb-2">New movie / TV show card</div>
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-2">What's it called?</h2>
        <p className="text-white/50 text-sm mb-6">Type the title. The site finds the real one and fills in the year, genre, IMDb and Rotten Tomatoes links.</p>

        <form onSubmit={(e) => { e.preventDefault(); lookup(); }} className="flex gap-3 mb-6">
          <input value={title} onChange={(e) => { setTitle(e.target.value); setInfo(null); }} autoFocus placeholder="e.g. Hawkeye" className="flex-1 !py-3.5 !text-base" data-testid="new-card-title" />
          <input value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Year" className="!w-24 !py-3.5" data-testid="new-card-year" />
          <button type="submit" disabled={looking || !title.trim()} className="btn-primary !px-5 disabled:opacity-50" data-testid="new-card-find">
            {looking ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />} Find it
          </button>
        </form>

        {info && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-5" data-testid="new-card-result">
            {info.found ? (
              <div className="flex gap-5">
                <div className="w-28 aspect-[2/3] rounded-lg overflow-hidden bg-[#232323] border border-white/10 flex-shrink-0">
                  {(info.poster_url) && <img src={info.poster_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  {info.existing && <div className="chip chip-garnet inline-flex"><CheckCircle size={11} weight="fill" /> Already on the site</div>}
                  <div className="font-display font-black text-2xl leading-tight">{info.title}</div>
                  <div className="text-xs text-white/55 flex flex-wrap gap-x-3 gap-y-1">
                    {info.year > 0 && <span>{info.year}</span>}
                    {info.type && <span>{info.type === "series" ? "TV series" : "Movie"}</span>}
                    {info.genre && <span>{info.genre}</span>}
                  </div>
                  <div className="flex items-center gap-4 pt-1">
                    {info.imdb_url && <a href={info.imdb_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:opacity-80"><ImdbBadge size={16} />{info.imdb_rating > 0 && <span className="font-display font-bold text-sm">{info.imdb_rating}</span>}<ArrowSquareOut size={11} className="text-white/40" /></a>}
                    {info.rt_url && <a href={info.rt_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:opacity-80"><RtBadge size={18} />{info.rt_rating > 0 && <span className="font-display font-bold text-sm">{info.rt_rating}%</span>}<span className={`text-[10px] font-mono ${info.rt_verified ? "text-[#7FE7A5]" : "text-[#FFB4BE]"}`}>{info.rt_verified ? "found" : "check"}</span></a>}
                  </div>
                  {info.synopsis && <p className="text-xs text-white/45 line-clamp-2">{info.synopsis}</p>}
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#FFB4BE]">We couldn't find this title automatically. You can still continue and fill in the details yourself.</div>
            )}

            {info.candidates?.length > 0 && !info.existing && (
              <select value="" onChange={(e) => e.target.value && pickCandidate(e.target.value)} className="mt-4 !py-2 !text-xs" data-testid="new-card-candidates">
                <option value="">Not the right one? Pick another…</option>
                {info.candidates.map((c) => <option key={c.imdb_id} value={c.imdb_id}>{c.title} ({c.year}) — {c.type === "series" ? "TV series" : c.type}</option>)}
              </select>
            )}

            <button type="button" onClick={continueWith} className="btn-primary w-full justify-center mt-5" data-testid="new-card-continue">
              {info.existing ? "Open this movie card" : "Create the card & add scenepacks"} <ArrowRight size={16} />
            </button>
          </motion.div>
        )}
      </div>
    );
  }

  // =====================================================================
  return (
    <div data-testid="movie-picker">
      <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#FF8090] mb-2">Step 1</div>
      <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-2">Where do these scenepacks go?</h2>
      <p className="text-white/50 text-sm mb-6 max-w-xl">Pick the movie or TV show, or create its card. Then you add the characters and their scenepacks inside it.</p>

      <div className="relative mb-4">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search movies & TV shows…" className="!pl-11 !py-3.5 !rounded-full !text-base" data-testid="picker-search" />
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="picker-detect"
        className={`mb-6 rounded-xl border border-dashed px-4 py-3 flex items-center gap-3 text-xs transition ${dragOver ? "border-[#FF8090] bg-[#FF8090]/[0.07] text-white" : "border-white/15 text-white/50"}`}
      >
        {detecting ? <CircleNotch size={16} className="animate-spin text-[#FF8090]" /> : <UploadSimple size={16} className="text-[#FF8090]" />}
        {detecting ? "Finding the movie…" : <span><b className="text-white/80">In a hurry?</b> Drop your scenepack files here and the site finds the movie for you.</span>}
      </div>

      {movies === null ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />)}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="picker-grid">
          <motion.button
            type="button"
            whileHover={{ y: -4 }}
            onClick={() => { setCreating(true); setTitle(query); }}
            data-testid="picker-new"
            className="aspect-[3/4] rounded-2xl border-2 border-dashed border-[#880015]/60 bg-gradient-to-b from-[#880015]/10 to-transparent hover:border-[#FF8090] hover:from-[#880015]/25 transition flex flex-col items-center justify-center gap-3 text-center p-4 group"
          >
            <span className="w-14 h-14 rounded-full bg-[#880015] flex items-center justify-center shadow-[0_0_30px_rgba(136,0,21,0.5)] group-hover:scale-110 transition"><Plus size={26} weight="bold" /></span>
            <span className="font-display font-bold text-sm leading-tight">New movie or<br />TV show card</span>
          </motion.button>

          <motion.button
            type="button"
            whileHover={{ y: -4 }}
            onClick={() => setCelebMode(true)}
            data-testid="picker-new-celebrity"
            className="aspect-[3/4] rounded-2xl border-2 border-dashed border-white/20 bg-gradient-to-b from-white/[0.04] to-transparent hover:border-[#FF8090] hover:from-[#880015]/20 transition flex flex-col items-center justify-center gap-3 text-center p-4 group"
          >
            <span className="w-14 h-14 rounded-full bg-[#2a1017] border border-[#880015]/60 flex items-center justify-center group-hover:scale-110 transition"><Star size={24} weight="fill" className="text-[#FF8090]" /></span>
            <span className="font-display font-bold text-sm leading-tight">New celebrity<br />card</span>
          </motion.button>

          {shown.map((m) => (
            <motion.button
              key={m.slug}
              type="button"
              layout
              whileHover={{ y: -4 }}
              onClick={() => onPick(cardFromMovie(m), [])}
              data-testid={`picker-movie-${m.slug}`}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 bg-[#1A1A1A] text-left group hover:border-[#FF8090]/60 hover:shadow-[0_18px_50px_-14px_rgba(136,0,21,0.6)] transition"
            >
              {m.thumbnail_url ? <SmartImg src={m.thumbnail_url} width={480} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="absolute inset-0 flex items-center justify-center"><FilmStrip size={36} className="text-white/20" /></div>}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3.5">
                <div className="font-display font-bold text-sm leading-tight line-clamp-2">{m.movie_title}</div>
                <div className="text-[10px] font-mono text-white/50 mt-1">{m.year || ""}{m.year ? " · " : ""}{m.characters_count} character{m.characters_count !== 1 ? "s" : ""} · {m.packs_count} pack{m.packs_count !== 1 ? "s" : ""}</div>
              </div>
            </motion.button>
          ))}
        </div>
      )}
      {movies && shown.length === 0 && query && <div className="text-white/40 font-mono text-xs text-center py-10">No movie called “{query}” yet — create its card with the first tile.</div>}
    </div>
  );
}
